import React, { useEffect, useState } from "react";

import { useForm } from "../useForm";
import NumberInputText from "../../controls/inputs/NumberInputText";
import DollarInputText from "../../controls/inputs/DollarInputText";
import Button from "../../controls/Button";
import {
  getOrderItemParentQuantity,
  getOrderItemUnitLabel,
  sumOrderItemSplitQuantities,
} from "../../../shared/orderItemPriceSplits";
import type {
  EmptyModalState,
  OrderItemForPriceSplit,
  OrderItemPriceSplit,
  PriceSplitPayload,
} from "../../../types/orders";

interface FormValues {
  quantity: number | null;
  unit_cost: number | null;
}

interface FormErrors {
  quantity?: string | null;
  unit_cost?: string | null;
  [key: string]: string | null | undefined;
}

interface OrderItemPriceSplitModalProps {
  orderItem: OrderItemForPriceSplit;
  split?: OrderItemPriceSplit | null;
  onConfirm: (payload: PriceSplitPayload) => void | Promise<void>;
  setOpen: (state: EmptyModalState) => void;
}

const initialFValues: FormValues = {
  quantity: null,
  unit_cost: null,
};

export default function OrderItemPriceSplitModal(
  props: OrderItemPriceSplitModalProps,
) {
  const { orderItem, split, onConfirm, setOpen } = props;
  const [submitting, setSubmitting] = useState(false);

  const unitLabel = getOrderItemUnitLabel(orderItem?.unit_type);
  const parentQuantity = getOrderItemParentQuantity(orderItem);
  const allocatedQuantity = sumOrderItemSplitQuantities(
    orderItem?.price_splits,
    split?.id ?? null,
  );
  const availableQuantity = Math.max(0, parentQuantity - allocatedQuantity);

  const validate = (fieldValues?: FormValues): boolean | void => {
    const current = fieldValues ?? values;
    const temp: FormErrors = { ...errors };

    temp.quantity = null;
    const quantity = parseFloat(String(current.quantity));
    if (current.quantity == null || Number.isNaN(quantity) || quantity <= 0) {
      temp.quantity = "Quantity must be greater than zero.";
    } else if (quantity > availableQuantity + 0.0001) {
      temp.quantity = `Quantity cannot exceed available ${availableQuantity} ${unitLabel}.`;
    }

    temp.unit_cost = null;
    const unitCost = parseFloat(String(current.unit_cost));
    if (current.unit_cost == null || Number.isNaN(unitCost) || unitCost < 0) {
      temp.unit_cost = "Unit cost cannot be negative.";
    }

    setErrors(temp);

    if (fieldValues === undefined || fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleMultipleInputChange } =
    useForm(initialFValues, false, validate) as unknown as {
      values: FormValues;
      setValues: (next: FormValues) => void;
      errors: FormErrors;
      setErrors: (next: FormErrors) => void;
      handleMultipleInputChange: (...parts: Partial<FormValues>[]) => void;
    };

  useEffect(() => {
    setValues({
      quantity: split ? parseFloat(String(split.quantity)) : null,
      unit_cost: split ? parseFloat(String(split.unit_cost)) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    if (submitting || !validate()) {
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm({
        quantity: parseFloat(String(values.quantity)),
        unit_cost: parseFloat(String(values.unit_cost)),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="justify-between">
      <div>
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
          <div className="mt-2">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {split ? "Edit price split" : "Add price split"}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {orderItem?.product_name} — {orderItem?.lot_number}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Available: {availableQuantity} {unitLabel}
            </p>
          </div>
        </div>

        <div className="pt-3 pb-5 gap-4">
          <div className="bg-white sm:rounded-xl">
            <div className="grid grid-cols-2 gap-4">
              <NumberInputText
                label="Quantity"
                name="quantity"
                onChange={(e: { target: { value: number | null } }) => {
                  setErrors({ ...errors, quantity: null });
                  handleMultipleInputChange({ quantity: e.target.value });
                }}
                value={values.quantity}
                labelOn={true}
                suffix={` ${unitLabel}`}
                error={errors.quantity}
                minFractionDigits={0}
                maxFractionDigits={4}
                disabled={submitting}
              />

              <DollarInputText
                label="Unit Cost"
                name="unit_cost"
                onChange={(e: { target: { value: number | null } }) => {
                  setErrors({ ...errors, unit_cost: null });
                  handleMultipleInputChange({ unit_cost: e.target.value });
                }}
                value={values.unit_cost}
                labelOn={true}
                error={errors.unit_cost}
                suffix={` /${unitLabel}`}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="sm:flex-no-wrap my-auto flex items-center">
            <Button
              className="mt-3"
              onClick={() => onSubmit()}
              loading={submitting}
            >
              {split ? "Update" : "Add"}
            </Button>
            <button
              onClick={() => setOpen({ childComponent: null, open: false })}
              disabled={submitting}
              className="ml-2 inline-flex justify-center mt-3 py-35-custom px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 items-center disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
